import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon,
  FieldText, FieldSelect,
} from './detail/DetailShell';
import { DOC_TYPES, TARGETS, PAPER_SIZES, emptyLayout } from './belgeDizayni/fieldCatalog';
import { usePageActions } from '../contexts/PermissionContext';
import { useDistributorScope } from '../hooks/useDistributorScope';

const API_BASE = '/api/documenttemplates';

const EMPTY_FORM = { name: '', docType: 2, target: 0, paperSize: 'A4', vendorId: '' };

const PAPER_OPTIONS = Object.entries(PAPER_SIZES).map(([k, v]) => ({ value: k, label: v.label }));

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}

export function BelgeDizayniCreatePage() {
  usePageMeta('Yeni Belge Dizaynı', 'Sipariş, İrsaliye ve Fatura belgeleri için yazdırılabilir düzen oluşturun.');
  const navigate = useNavigate();
  // Bu ekran yalnızca yeni dizayn oluşturur (id yok) → kaydetme yetkisi = ekleme yetkisi.
  const { canAdd } = usePageActions();
  const canSave = canAdd;

  const [form, setForm] = useState(EMPTY_FORM);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Talep 834 — Distribütör kapsamı; seçenekler kullanıcının yetkili olduğu distribütörler.
  const { vendorOptions, lockedOptions, isDistributorUser, lockedVendorId, scopeLoaded } = useDistributorScope();

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Distribütör tipi kullanıcı: yetkili distribütör otomatik seçili + DEĞİŞTİRİLEMEZ.
  useEffect(() => {
    if (!scopeLoaded || !lockedVendorId) return;
    setForm((c) => (String(c.vendorId ?? '') === lockedVendorId ? c : { ...c, vendorId: lockedVendorId }));
  }, [scopeLoaded, lockedVendorId]);

  // Oluştur: kayıt OLUŞTUR, dönen id ile designer'a (tam-sayfa tasarım) git.
  // Kullanıcı "Oluştur" diyene kadar hiçbir şey kaydedilmez.
  const handleCreate = async () => {
    setTried(true);
    if (!form.name.trim()) { showToast('error', 'Dizayn adı zorunludur'); return; }
    setSaving(true);
    try {
      const layout = emptyLayout(form.paperSize, 0);
      const res = await fetch(`${API_BASE}/Add`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          docType: Number(form.docType),
          target: Number(form.target),
          paperSize: form.paperSize,
          orientation: 0,
          // Boş seçim = genel dizayn → sunucuya null gitmeli ('' Guid?'e bağlanmaz).
          vendorId: form.vendorId || null,
          layoutJson: JSON.stringify(layout),
        }),
      });
      if (!res.ok) throw new Error('Dizayn oluşturulamadı');
      const json = await res.json().catch(() => null);
      const id = pick(json?.data, 'id') || pick(json?.Data, 'id');
      if (id) navigate(`/belge-dizayni/${id}`);
      else navigate('/belge-dizayni');
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
      setSaving(false);
    }
  };

  const actions = (
    <>
      <button className="dps-btn ghost" onClick={() => navigate('/belge-dizayni')} disabled={saving}>İptal</button>
      {canSave && (
        <button className="dps-btn primary" onClick={handleCreate} disabled={saving}>
          <Icon name="check" size={17} /> {saving ? 'Oluşturuluyor…' : 'Oluştur'}
        </button>
      )}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Belge Dizaynı', to: '/belge-dizayni' }, { label: 'Yeni Belge', current: true }]}
      actions={actions}
    >
      <div className="dps-form">
        <Section icon="layers" title="Belge Dizaynı Bilgileri" subtitle="Belge türü, hedef yazıcı ve kağıt boyutunu seçin">
          <Grid cols={2}>
            <FieldText form={form} set={set} tried={tried} k="name" label="Dizayn Adı" req placeholder="Örn. Standart Fatura A4" span />
            <FieldSelect form={form} set={set} k="docType" label="Belge Türü" options={DOC_TYPES} />
            {/* Talep 834 — Distribütör; zorunlu DEĞİL. Boş = tüm distribütörler için geçerli genel
                dizayn, seçili = yalnız o distribütör için. Distribütör tipi kullanıcıda yetkili
                distribütör otomatik seçili ve değiştirilemez. */}
            <FieldSelect
              form={form} set={set} k="vendorId" label="Distribütör"
              options={isDistributorUser && lockedVendorId ? lockedOptions : vendorOptions}
              disabled={Boolean(isDistributorUser && lockedVendorId)}
              hint={isDistributorUser && lockedVendorId
                ? 'Distribütör tipi kullanıcı — yetkili distribütörünüz otomatik seçildi.'
                : 'Boş bırakılırsa tüm distribütörler için geçerli olur.'}
            />
            <FieldSelect form={form} set={set} k="target" label="Hedef Yazıcı" options={TARGETS} />
            <FieldSelect form={form} set={set} k="paperSize" label="Kağıt Boyutu" options={PAPER_OPTIONS} />
          </Grid>
        </Section>
      </div>

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default BelgeDizayniCreatePage;
